import {
  Box,
  FormControl,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { Button, TextField, Typography } from "@platform/design-system";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/** Treat "" and "-" as empty; otherwise parse and clamp to [0, 100]. */
function parseClampedPercent(raw: string): number | null {
  if (raw === "" || raw === "-") {
    return null;
  }
  const n = parseFloat(raw);
  return isNaN(n) ? null : Math.max(0, Math.min(100, n));
}

type GradedMode = "none" | "percent";

export interface GradedCommissionEditorRef {
  getValue: () => number | null;
  isPopup?: () => boolean;
}

/**
 * Popover-based editor for graded commission. Unlike heaped, graded stores a
 * single plain number (the highest %) or null when no percentage is defined.
 */
export const GradedCommissionEditor = forwardRef<
  GradedCommissionEditorRef,
  CustomCellEditorProps
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const onValueChange = (
    props as unknown as {
      onValueChange?: (value: number | null) => void;
    }
  ).onValueChange;

  // Parse the value - it might be a JSON string that needs parsing.
  // A numeric value means "percent" mode; anything else means "none".
  let initialValue: number | null = null;
  if (typeof props.value === "number") {
    initialValue = props.value;
  } else if (typeof props.value === "string") {
    try {
      const parsed = JSON.parse(props.value) as unknown;
      if (typeof parsed === "number") {
        initialValue = parsed;
      }
    } catch {
      initialValue = null;
    }
  }

  const [mode, setMode] = useState<GradedMode>(
    initialValue === null ? "none" : "percent",
  );
  const [highest, setHighest] = useState<string>(
    initialValue === null ? "" : initialValue.toString(),
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const computeValue = (): number | null =>
    mode === "none" ? null : parseClampedPercent(highest);

  useImperativeHandle(ref, () => ({
    getValue: () => computeValue(),
    isPopup: () => true,
  }));

  useEffect(() => {
    setOpen(true);
  }, []);

  const handleSave = (): void => {
    const finalValue = computeValue();

    // Prefer AG Grid's editor callback when available; this is the native
    // commit path for React custom editors.
    if (onValueChange) {
      onValueChange(finalValue);
    } else if (node && column) {
      // Fallback for older editor integration paths.
      const columnField = column.getColId();
      node.setDataValue(columnField, finalValue);
    }

    setOpen(false);
    if (api) {
      api.stopEditing();
    } else {
      stopEditing();
    }
  };

  const handleEditorMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
  };

  const handleCancel = (): void => {
    setOpen(false);
    stopEditing(true);
    api?.stopEditing(true);
  };

  return (
    <Box ref={anchorRef}>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        onMouseDown={handleEditorMouseDown}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          paper: {
            className: "ag-custom-component-popup",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="header-small" gutterBottom>
            Graded Commission
          </Typography>

          <Stack spacing={2}>
            <FormControl>
              <RadioGroup
                value={mode}
                onChange={(e): void => {
                  const nextMode = e.target.value as GradedMode;
                  setMode(nextMode);
                  // "Percent graded" needs a value to operate on, so seed the
                  // field with 1 when switching to it from an empty state.
                  if (nextMode === "percent" && highest === "") {
                    setHighest("1");
                  }
                }}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label="No defined percentage"
                />
                <FormControlLabel
                  value="percent"
                  control={<Radio />}
                  label="Percent graded"
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Highest %"
              type="number"
              value={highest}
              disabled={mode === "none"}
              onChange={(e): void => {
                const value = e.target.value;
                const numValue = parseFloat(value);
                // Allow empty string for editing, otherwise clamp
                if (value === "" || value === "-") {
                  setHighest(value);
                } else if (!isNaN(numValue)) {
                  const clamped = Math.max(0, Math.min(100, numValue));
                  setHighest(clamped.toString());
                }
              }}
              onBlur={(e): void => {
                // On blur, allow empty values (no percentage required)
                const numValue = parseFloat(e.target.value);
                if (e.target.value === "" || e.target.value === "-") {
                  setHighest("");
                } else if (isNaN(numValue)) {
                  setHighest("");
                } else {
                  const clamped = Math.max(0, Math.min(100, numValue));
                  setHighest(clamped.toString());
                }
              }}
              inputProps={{
                min: 0,
                max: 100,
                step: 0.01,
              }}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button onClick={handleCancel} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary">
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
});

GradedCommissionEditor.displayName = "GradedCommissionEditor";
